import { z } from 'zod';

/**
 * Runtime-validated environment schema.
 * Per docs/architecture/02-security-architecture.md: only the public,
 * client-safe Supabase anon key is validated here — never a service role key.
 */
export const envSchema = z.object({
  VITE_SUPABASE_URL: z.string().url({ message: 'VITE_SUPABASE_URL must be a valid URL' }),
  VITE_SUPABASE_ANON_KEY: z.string().min(1, 'VITE_SUPABASE_ANON_KEY is required'),
  VITE_APP_ENV: z.enum(['development', 'staging', 'production']).default('development'),
  // Used only at build time by scripts/generate-seo-files.mjs to produce
  // absolute URLs in sitemap.xml/robots.txt. Optional because local dev and
  // typecheck/test runs never need it; the script falls back to a clearly
  // fake placeholder domain (and warns loudly) if it's unset, rather than
  // guessing the real production domain.
  VITE_SITE_URL: z.string().url({ message: 'VITE_SITE_URL must be a valid URL' }).optional()
});

export type Env = z.infer<typeof envSchema>;

/**
 * Validates and returns the current environment configuration.
 * Throws early (fail-fast at startup) if a required variable is missing or malformed,
 * rather than failing silently deep inside the application.
 */
export function loadEnv(source: Record<string, string | undefined>): Env {
  const result = envSchema.safeParse(source);
  if (!result.success) {
    const issues = result.error.issues.map((issue) => `- ${issue.path.join('.')}: ${issue.message}`);
    throw new Error(`Invalid environment configuration:\n${issues.join('\n')}`);
  }
  return result.data;
}
